import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { twMerge } from 'tailwind-merge';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    placement?: Placement;
    children: ReactNode;
}

/**
 * A popover anchored to `anchorEl`, portaled out of any clipped/overflow parent,
 * closing on Escape or a pointer down outside the panel and its anchor. The
 * anchor is excluded from the outside-close so the trigger's own click can
 * toggle the popover shut, instead of this closing it first and the trigger
 * immediately reopening it.
 */
const AnchoredPopover = ({
    anchorEl,
    onClose,
    placement = 'top-start',
    className,
    children,
    ...rest
}: Props) => {
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
        null,
    );

    const { styles, attributes } = usePopper(anchorEl, popperElement, {
        placement,
        modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'flip', options: { flipVariations: true } },
            { name: 'preventOverflow', options: { padding: 8 } },
        ],
    });

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        const handlePointerDown = (event: Event): void => {
            const target = event.target as Node;
            if (popperElement?.contains(target) || anchorEl?.contains(target)) {
                return;
            }
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [onClose, anchorEl, popperElement]);

    return createPortal(
        <div
            {...rest}
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className={twMerge('z-50', className)}
        >
            {children}
        </div>,
        document.getElementById('contextMenuPortal') || document.body,
    );
};

export default AnchoredPopover;
